/**
 * Instagram-style profile feed: sticky tabs + ordered mix of cards.
 *
 * Order (F-170 B2.3): Info → all services → all portfolio → Q&A. The
 * Reviews tab was retired ahead of the F-180 reviews redesign — the
 * backend model + `ReviewCard` component stay, but the public feed no
 * longer surfaces them.
 *
 * "Спитати" / "Замовити" on service cards both open a chat with a prefilled
 * first message (POST /chats/ ?text=...). The actual order proposal is sent
 * by the specialist via OrderProposalComposer.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { SpecialistProfile, ServicePreset } from '@/types/domain';
import { chats as chatsApi } from '@/api';
import { toast } from '@/components/atoms/Toast';
import { logger } from '@/utils/logger';
import { BookSlotModal } from '../BookSlotModal';
import { ProductCard } from '../ProductCard';
import { ServiceOrderForm } from '../ServiceOrderForm';
import { ServiceAreaMap } from '@/components/atoms/ServiceAreaMap';
import { InfoCard } from './InfoCard';
import { ServiceCard } from './ServiceCard';
import { PortfolioPostCard } from './PortfolioPostCard';
import { QnACard } from './QnACard';
import { FullscreenViewer } from './FullscreenViewer';
import styles from './ProfileFeed.module.css';

type Tab = 'all' | 'services' | 'products' | 'portfolio';

export interface ProfileFeedProps {
  profile: SpecialistProfile;
}

export function ProfileFeed({ profile }: ProfileFeedProps): JSX.Element {
  const navigate = useNavigate();
  // F-170 B4.1: surface the Товари tab only when the specialist actually
  // sells anything, so service-only profiles keep the original 3-tab layout.
  const hasProducts = (profile.products?.length ?? 0) > 0;
  const TABS: { value: Tab; label: string }[] = [
    { value: 'all', label: 'Усе' },
    { value: 'services', label: 'Послуги' },
    ...(hasProducts ? [{ value: 'products' as const, label: 'Товари' }] : []),
    { value: 'portfolio', label: 'Роботи' },
  ];
  const [tab, setTab] = useState<Tab>('all');
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  const [bookPreset, setBookPreset] = useState<ServicePreset | null>(null);
  // F-170 B4.4: non-time-based services route through a dedicated order form
  // instead of opening a chat with a prefilled message.
  const [orderPreset, setOrderPreset] = useState<ServicePreset | null>(null);
  /** F-160 (C6): the backend now exposes a cached `has_bookable_slots`
   *  boolean on `SpecialistDetailSerializer`, so we drop the per-render
   *  /slots/ probe and just read the flag off `profile`. */
  const bookable = profile.has_bookable_slots ?? false;

  const openChatWithPrefill = async (
    preset: ServicePreset,
    intent: 'ask' | 'order',
  ): Promise<void> => {
    const text =
      intent === 'order'
        ? `Хочу замовити «${preset.title}» — коли зручно?`
        : `Цікавить «${preset.title}» — підкажи деталі`;
    try {
      const chat = await chatsApi.openChatWithSpecialist(profile.slug, { firstText: text });
      navigate(`/chats/${chat.id}`);
    } catch (err) {
      logger.warn('openChatWithSpecialist failed', err);
      toast.error('Не вдалось відкрити чат');
    }
  };

  const sections = useMemo(() => {
    const services = profile.presets ?? [];
    const portfolio = profile.portfolio ?? [];
    const qna = profile.qna ?? [];
    const products = profile.products ?? [];
    return { services, portfolio, qna, products };
  }, [profile]);

  const showInfo = tab === 'all';
  const showServices = tab === 'all' || tab === 'services';
  const showProducts = (tab === 'all' || tab === 'products') && hasProducts;
  const showPortfolio = tab === 'all' || tab === 'portfolio';
  const showQna = tab === 'all'; // Q&A только в "Усе"

  return (
    <>
      <div className={styles.tabs} role="tablist">
        {TABS.map((t) => (
          <button
            key={t.value}
            role="tab"
            aria-selected={tab === t.value}
            className={tab === t.value ? `${styles.tab} ${styles.tabActive}` : styles.tab}
            onClick={() => setTab(t.value)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className={styles.feed}>
        {showInfo ? <InfoCard profile={profile} /> : null}

        {/* F-170 B3.5: read-only Maplibre map per geo zone the specialist
            declared. Only renders in the "Усе" tab — drilling into
            services / portfolio shouldn't include the map. */}
        {showInfo && (profile.service_areas?.length ?? 0) > 0
          ? profile.service_areas!.map((area) => (
              <article key={`area-${area.id}`} className={styles.card}>
                <h3 style={{ margin: '0 0 10px', fontSize: 16, fontWeight: 600 }}>
                  Зона обслуговування
                </h3>
                <ServiceAreaMap mode="view" value={area} />
              </article>
            ))
          : null}

        {showServices &&
          sections.services.map((preset) => {
            // F-170 B2.5: "Записатись" only when the preset has a duration
            // AND happens off `online` (online services don't reserve a
            // physical slot — they're just async chat). Falls back to
            // "Замовити" otherwise.
            const presetBookable =
              bookable &&
              (preset.duration_minutes ?? 0) > 0 &&
              preset.location_kind !== 'online';
            return (
              <ServiceCard
                key={`service-${preset.id}`}
                preset={preset}
                bookable={presetBookable}
                onAsk={() => openChatWithPrefill(preset, 'ask')}
                // F-190 QA: "Замовити" ALWAYS creates an order via
                // ServiceOrderForm (status="created", per-order chat
                // with order_proposal widget). Previous fallback to
                // openChatWithPrefill left bookable-but-slot-less
                // services with no way to actually place an order —
                // user just landed in chat with prefilled text and
                // nothing on the specialist's side.
                onOrder={() => setOrderPreset(preset)}
                onBook={() => setBookPreset(preset)}
              />
            );
          })}

        {showProducts &&
          sections.products.map((product) => (
            <ProductCard
              key={`product-${product.id}`}
              product={product}
              specialistId={profile.slug}
            />
          ))}

        {showPortfolio &&
          sections.portfolio.map((story, i) => (
            <PortfolioPostCard
              key={`story-${story.id}`}
              story={story}
              onOpen={() => setViewerIndex(i)}
            />
          ))}

        {showQna && sections.qna.map((q) => <QnACard key={`qna-${q.id}`} item={q} />)}

        {tab === 'all' &&
          sections.services.length === 0 &&
          sections.portfolio.length === 0 ? (
          <p className={styles.emptyHint}>
            Поки що тут пусто. Напиши спеціалісту — обговориш деталі.
          </p>
        ) : null}
      </div>

      {viewerIndex !== null ? (
        <FullscreenViewer
          stories={sections.portfolio}
          startIndex={viewerIndex}
          onClose={() => setViewerIndex(null)}
        />
      ) : null}

      {bookPreset ? (
        <BookSlotModal
          open
          onClose={() => setBookPreset(null)}
          specialistSlug={profile.slug}
          preset={bookPreset}
        />
      ) : null}

      {orderPreset ? (
        <ServiceOrderForm
          open
          preset={orderPreset}
          specialistSlug={profile.slug}
          onClose={() => setOrderPreset(null)}
          onCreated={(chatId) => {
            setOrderPreset(null);
            navigate(`/chats/${chatId}`);
          }}
        />
      ) : null}
    </>
  );
}
